import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Logo from '../assets/logo/logo.png';

/* ─── Particle background ─────────────────────────────────────────────── */
const ParticleCanvas = () => {
  useEffect(() => {
    const canvas = document.getElementById('particle-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = (canvas.width = window.innerWidth);
    let H = (canvas.height = window.innerHeight);
    const N = 90;

    const particles = Array.from({ length: N }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1.8 + 0.3,
      dx: (Math.random() - 0.5) * 0.4,
      dy: (Math.random() - 0.5) * 0.4,
      alpha: Math.random() * 0.5 + 0.15,
    }));

    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > W) p.dx *= -1;
        if (p.y < 0 || p.y > H) p.dy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(100,160,255,${p.alpha})`;
        ctx.fill();
      });

      // connections
      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(80,130,255,${0.12 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };

    draw();

    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      id="particle-canvas"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

/* ─── Floating orbs ───────────────────────────────────────────────────── */
const Orb = ({ style }) => (
  <Box
    sx={{
      position: 'fixed',
      borderRadius: '50%',
      filter: 'blur(80px)',
      pointerEvents: 'none',
      zIndex: 0,
      ...style,
    }}
  />
);

/* ─── Main Login ──────────────────────────────────────────────────────── */
const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // staggered entrance
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://dimspos.com/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });

      if (!response.ok) {
        throw new Error(
          response.status === 401 || response.status === 400
            ? 'Invalid email or password. Please try again.'
            : 'Server error. Please try later.'
        );
      }

      const data = await response.json();

      if (data.token) {
        localStorage.setItem('token', data.token);
        sessionStorage.setItem('email', formData.email);
        localStorage.setItem('isAuthenticated', 'true');

        if (formData.email === 'Albina@booncoffee.com') navigate('/albina/home');
        else if (formData.email === 'odoo-admin@alfaroojfresh.com') navigate('/alfaroojfresh/home');
        else navigate('/albina/home');
      } else {
        throw new Error(data.message || 'Login failed. No token provided.');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* shared sx for text fields */
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      color: '#e8f0ff',
      background: 'rgba(10,22,60,0.55)',
      backdropFilter: 'blur(6px)',
      transition: 'box-shadow 0.25s',
      '& fieldset': { borderColor: 'rgba(100,150,255,0.25)' },
      '&:hover fieldset': { borderColor: 'rgba(100,150,255,0.55)' },
      '&.Mui-focused fieldset': {
        borderColor: '#4d8cff',
        boxShadow: '0 0 0 3px rgba(77,140,255,0.18)',
      },
    },
    '& .MuiInputLabel-root': { color: 'rgba(160,190,255,0.7)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#7aabff' },
  };

  return (
    <>
      {/* ── Background layer ── */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          background:
            'radial-gradient(ellipse at 20% 50%, #051230 0%, #020b1e 55%, #000810 100%)',
          zIndex: -1,
        }}
      />
      <ParticleCanvas />
      <Orb
        style={{
          width: 600,
          height: 600,
          top: '-180px',
          left: '-200px',
          background: 'rgba(20,60,180,0.22)',
        }}
      />
      <Orb
        style={{
          width: 500,
          height: 500,
          bottom: '-150px',
          right: '-150px',
          background: 'rgba(10,40,160,0.18)',
        }}
      />
      <Orb
        style={{
          width: 280,
          height: 280,
          top: '38%',
          left: '60%',
          background: 'rgba(40,100,255,0.1)',
        }}
      />

      {/* ── Page wrapper ── */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          px: 2,
          py: 4,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            mb: 4,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(-24px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <img alt="logo" src={Logo} style={{ width: 200, filter: 'brightness(1.1) drop-shadow(0 0 18px rgba(80,140,255,0.45))' }} />
        </Box>

        {/* Card */}
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 460,
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(80,130,255,0.18)',
            background: 'rgba(8,18,50,0.72)',
            backdropFilter: 'blur(28px)',
            boxShadow:
              '0 30px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0) scale(1)' : 'translateY(32px) scale(0.97)',
            transition: 'opacity 0.65s ease 0.1s, transform 0.65s ease 0.1s',
          }}
        >
          {/* Top accent bar */}
          <Box
            sx={{
              height: 4,
              background: 'linear-gradient(90deg, #1a3fff 0%, #4d8cff 50%, #00c6ff 100%)',
            }}
          />

          <Box sx={{ p: { xs: 3.5, sm: 5 } }}>
            {/* Heading */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Rajdhani", "Oswald", sans-serif',
                  fontWeight: 700,
                  fontSize: '2rem',
                  letterSpacing: '0.04em',
                  color: '#e8f0ff',
                  lineHeight: 1.1,
                  mb: 0.5,
                }}
              >
                Welcome back
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(140,170,255,0.7)',
                  fontSize: '0.88rem',
                  letterSpacing: '0.03em',
                }}
              >
                Sign in to your Dimensions Plus account
              </Typography>
            </Box>

            {/* Error */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: '12px',
                  background: 'rgba(220,50,50,0.14)',
                  border: '1px solid rgba(220,80,80,0.3)',
                  color: '#ffaaaa',
                  '& .MuiAlert-icon': { color: '#ff6b6b' },
                }}
              >
                {error}
              </Alert>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: 'rgba(120,160,255,0.6)', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />

              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: 'rgba(120,160,255,0.6)', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'rgba(120,160,255,0.5)', '&:hover': { color: '#7aabff' } }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />

              {/* Sign-in button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                endIcon={!loading && <ArrowForwardIcon />}
                sx={{
                  mt: 1,
                  py: 1.7,
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontFamily: '"Rajdhani", sans-serif',
                  background: 'linear-gradient(135deg, #1a3fff 0%, #4d8cff 60%, #00aaff 100%)',
                  boxShadow: '0 6px 30px rgba(30,80,255,0.45)',
                  border: '1px solid rgba(100,170,255,0.25)',
                  transition: 'box-shadow 0.3s, transform 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2248ff 0%, #5d9aff 60%, #10c0ff 100%)',
                    boxShadow: '0 8px 40px rgba(30,80,255,0.65)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': { transform: 'translateY(0)' },
                  '&.Mui-disabled': {
                    background: 'rgba(40,70,180,0.35)',
                    color: 'rgba(160,190,255,0.4)',
                  },
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: 'rgba(200,220,255,0.7)' }} /> : 'Sign In'}
              </Button>
            </Box>

            {/* Divider */}
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: '1px solid rgba(80,120,255,0.13)',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: 'rgba(100,140,220,0.45)', fontSize: '0.78rem', letterSpacing: '0.04em' }}
              >
                © {new Date().getFullYear()} Dimensions Plus. All rights reserved.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default Login;